import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { User, Flag, Edit, File, Clipboard, FileText } from "lucide-react";
import { toast } from "sonner";
import { useSendEnhancedMessageMutation } from "../api/CommunicationApi";
import {
  getGrades,
  saveRubrics,
  ProgressDetailsResponse,
  GradeSubmissionRequest,
  GradeResponse,
} from "../api/createGradeApi";

// safe number coercion: returns a finite number or 0
const toFinite = (v: any): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/* ----------------------------- UI types --------------------------------- */

interface RubricItemUI {
  criteria: string;
  earnedPoints: number;
  points: number;
}

interface RubricUI {
  id: string;
  checkPointName: string;
  rubricItems: RubricItemUI[];
  checkpointFeedback: string;
  checkPointTotal: number;
}

interface FeedbackSection {
  students: { studentId: string; studentName: string; studentEmail: string }[];
  assignment: {
    assignmentId: string;
    assignMentName: string;
    courseName: string;
  };
  projectTitle: string;
  courseCode: string;
  courseName: string;
  rubrics: RubricUI[];
  totalScore: number;
  overAllFeedback: string;
  projectType?: boolean;
}

interface FeedbackDialogProps {
  isOpen: boolean;
  onClose: () => void;
  submissionId?: string | undefined;
  onSubmit?: (
    serverPayload: GradeResponse | any,
    opts?: { source?: string },
  ) => void;
}

/* -------------------------- mock fallback ------------------------------- */

const mockFeedbackSection: FeedbackSection = {
  students: [],
  assignment: { assignmentId: "", assignMentName: "", courseName: "" },
  projectTitle: "",
  courseCode: "",
  courseName: "",
  rubrics: [
    {
      id: "",
      checkPointName: "",
      rubricItems: [{ criteria: "", earnedPoints: 0, points: 0 }],
      checkpointFeedback: "",
      checkPointTotal: 0,
    },
  ],
  totalScore: 0,
  overAllFeedback: "",
};

const getGradeColor = (score: number, maxScore: number) => {
  const percentage = (score / maxScore) * 100;
  if (percentage >= 90) return "text-green-600";
  if (percentage >= 80) return "text-blue-600";
  if (percentage >= 70) return "text-yellow-600";
  return "text-red-600";
};

/* --------------------- typed mapper: ProgressDetailsResponse -> UI ------- */

function mapApiToFeedbackSectionTyped(
  apiResp: ProgressDetailsResponse | undefined | null,
): FeedbackSection {
  if (!apiResp) return mockFeedbackSection;
  const payload = apiResp.data;

  const students = payload.student
    ? [
        {
          studentId: payload.student.id,
          studentName: payload.student.name,
          studentEmail: payload.student.email,
        },
      ]
    : [];
  const assignment = {
    assignmentId: payload.project?.id ?? "",
    assignMentName: payload.project?.title ?? "",
    courseName: payload.project?.course?.name ?? "",
  };

  const checkpoints = Array.isArray(payload.checkpoints)
    ? payload.checkpoints
    : [];

  const rubricsUI = checkpoints.map((cp) => {
    const criteriaList: {
      criterionName: string;
      score: number;
      max: number;
    }[] = [];

    if (Array.isArray(cp.rubrics)) {
      for (const rubric of cp.rubrics) {
        if (!Array.isArray(rubric.criteria)) continue;
        for (const crit of rubric.criteria) {
          const name = crit.description ?? crit.name ?? "Criterion";

          // Handle different currentScore structures
          let score = 0;
          if (typeof crit.currentScore === "number") {
            score = crit.currentScore;
          } else if (
            typeof crit.currentScore === "object" &&
            crit.currentScore !== null
          ) {
            // Type assertion for the object structure
            const scoreObj = crit.currentScore as any;
            score = toFinite(scoreObj.score);
          }

          const max = toFinite(crit.points);

          // Keep each criterion separate instead of merging by name
          criteriaList.push({
            criterionName: name,
            score: score,
            max: max,
          });
        }
      }
    }

    const rubricItems: RubricItemUI[] = criteriaList.map((v) => ({
      criteria: v.criterionName,
      earnedPoints: toFinite(v.score),
      points: toFinite(v.max),
    }));

    const aggregatedMax = rubricItems.reduce((s, it) => s + it.points, 0);

    return {
      id: cp.id,
      checkPointName:
        cp.title ?? cp.description ?? `Checkpoint ${cp.checkpointNumber}`,
      rubricItems,
      checkpointFeedback: cp.checkpointFeedback ?? "",
      checkPointTotal: aggregatedMax,
    } as RubricUI;
  });

  const totalScore = Number(payload.gradeDetails?.totalScore ?? 0) || 0;
  const overAllFeedback = payload.gradeDetails?.overallFeedback ?? "";

  const projectTitle = payload.project?.title ?? "";
  const courseCode = payload.project?.course?.code ?? "";
  const courseName = payload.project?.course?.name ?? "";
  const projectType = (payload?.project as any)?.isGroupProject;
  return {
    students,
    assignment,
    projectTitle,
    courseCode,
    courseName,
    rubrics: rubricsUI,
    totalScore,
    overAllFeedback,
    projectType,
  };
}

/* ---------------------------- component --------------------------------- */

export default function FeedbackDialog({
  isOpen,
  onClose,
  submissionId,
  onSubmit,
}: FeedbackDialogProps) {
  const queryClient = useQueryClient();
  const savingRef = React.useRef(false);

  const {
    data: apiData,
    isLoading,
    isError,
  } = useQuery<ProgressDetailsResponse, Error>({
    queryKey: ["progressDetails", submissionId],
    queryFn: ({ queryKey }) => {
      const [, id] = queryKey as [string, string | undefined];
      if (!id) throw new Error("Missing submissionId");
      return getGrades(id);
    },
    enabled: !!submissionId && isOpen,
    retry: 1,
  });
  const sendMessageMutation = useSendEnhancedMessageMutation();
  const [feedbackSections, setFeedbackSections] =
    useState<FeedbackSection | null>(null);
  const [openCheckpoints, setOpenCheckpoints] = useState<
    Record<string, boolean>
  >({});
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [checkpointEditable, setCheckpointEditable] = useState<
    Record<string, boolean>
  >({});
  const [showEditConfirmation, setShowEditConfirmation] = useState(false);
  const [pendingEdit, setPendingEdit] = useState<{
    checkpointId: string;
    criteriaIndex: number;
    value: number;
  } | null>(null);
  const [originalFeedbackSections, setOriginalFeedbackSections] =
    useState<FeedbackSection | null>(null);

  useEffect(() => {
    if (isLoading) return;
    if (apiData) {
      const mapped = mapApiToFeedbackSectionTyped(apiData);
      setFeedbackSections(mapped);
      setOriginalFeedbackSections(JSON.parse(JSON.stringify(mapped))); // Deep copy for comparison
      const initOpen: Record<string, boolean> = {};
      mapped.rubrics.forEach((r) => (initOpen[r.id] = false));
      setOpenCheckpoints(initOpen);
    } else if (!submissionId) {
      setFeedbackSections(mockFeedbackSection);
      setOriginalFeedbackSections(
        JSON.parse(JSON.stringify(mockFeedbackSection)),
      );
      const initOpen: Record<string, boolean> = {};
      mockFeedbackSection.rubrics.forEach((r) => (initOpen[r.id] = false));
      setOpenCheckpoints(initOpen);
    }
    if (apiData?.data?.checkpoints) {
      const editableMap: Record<string, boolean> = {};
      apiData.data.checkpoints.forEach((cp) => {
        editableMap[cp.id] = Boolean(cp.isCompleted);
      });
      setCheckpointEditable(editableMap);
    } else {
      setCheckpointEditable({});
    }
  }, [apiData, isLoading, submissionId]);

  const totalEarned = useMemo(() => {
    if (!feedbackSections) return 0;
    return feedbackSections.rubrics.reduce(
      (acc, cp) =>
        acc +
        cp.rubricItems.reduce((s, it) => s + toFinite(it.earnedPoints), 0),
      0,
    );
  }, [feedbackSections]);

  const totalMax = useMemo(() => {
    if (!feedbackSections) return 0;
    return feedbackSections.rubrics.reduce(
      (acc, cp) =>
        acc + cp.rubricItems.reduce((s, it) => s + toFinite(it.points), 0),
      0,
    );
  }, [feedbackSections]);

  // Helper function to check if rubric is evaluated (has any earned points)
  const isRubricEvaluated = (rubric: RubricUI): boolean => {
    return rubric.rubricItems.some((item) => toFinite(item.earnedPoints) > 0);
  };

  function applyGradeResponseToUI(gradeData: any): FeedbackSection {
    if (!gradeData) return mockFeedbackSection;

    const checkpointBreakdown = gradeData.checkpointBreakdown ?? {};
    const rubricsArr: RubricUI[] = [];

    for (const [checkpointId, cpObj] of Object.entries<any>(
      checkpointBreakdown,
    )) {
      const criteriaList: {
        criterionName: string;
        score: number;
        max: number;
      }[] = [];

      if (Array.isArray(cpObj.rubrics)) {
        for (const rubric of cpObj.rubrics) {
          if (!Array.isArray(rubric.criteriaScores)) continue;
          for (const cs of rubric.criteriaScores) {
            const name = String(
              cs.criterionName ?? cs.criterion ?? "Criterion",
            );
            const score = Number(cs.score ?? 0);
            const max = Number(cs.maxPoints ?? cs.maxScore ?? 0);

            // Keep each criterion separate instead of merging by name
            criteriaList.push({
              criterionName: name,
              score: score,
              max: max,
            });
          }
        }
      }

      const items: RubricItemUI[] = criteriaList.map((v) => ({
        criteria: v.criterionName,
        earnedPoints: v.score,
        points: v.max,
      }));

      const checkpointTitle =
        cpObj.checkpointTitle ??
        cpObj.checkpointName ??
        `Checkpoint ${checkpointId}`;
      const aggregatedMax = items.reduce((s, it) => s + (it.points ?? 0), 0);

      rubricsArr.push({
        id: checkpointId,
        checkPointName: checkpointTitle,
        rubricItems: items,
        checkpointFeedback: cpObj.checkpointFeedback ?? "",
        checkPointTotal: aggregatedMax,
      });
    }

    const projectTitle =
      gradeData?.project?.title ?? gradeData?.projectTitle ?? "";
    const courseCode =
      gradeData?.project?.course?.code ?? gradeData?.courseCode ?? "";
    const courseName =
      gradeData?.project?.course?.name ?? gradeData?.courseName ?? "";

    return {
      students: mockFeedbackSection.students,
      projectTitle,
      courseCode,
      courseName,
      assignment: mockFeedbackSection.assignment,
      rubrics: rubricsArr,
      totalScore: Number(gradeData.totalScore ?? gradeData.totalPoints ?? 0),
      overAllFeedback: gradeData.feedback ?? gradeData.overAllFeedback ?? "",
    };
  }

  const mutation = useMutation({
    mutationFn: (body: GradeSubmissionRequest) => saveRubrics(body),
    retry: 0,
    onError: (err: any) => {
      savingRef.current = false;
    },
    onSuccess: (resp: any) => {
      savingRef.current = false;

      const serverPayload =
        resp && (resp as any).data ? (resp as any).data : resp;
      const gradeData = serverPayload?.data ?? serverPayload;

      toast.success(serverPayload?.message ?? "Grade saved successfully");

      const mapped = applyGradeResponseToUI(gradeData);
      setFeedbackSections((prev) => ({
        students: prev?.students ?? mapped.students,
        projectTitle: prev?.projectTitle ?? mapped.projectTitle,
        courseCode: prev?.courseCode ?? mapped.courseCode,
        courseName: prev?.courseName ?? mapped.courseName,
        assignment: prev?.assignment ?? mapped.assignment,
        rubrics: mapped.rubrics,
        totalScore: mapped.totalScore,
        overAllFeedback: mapped.overAllFeedback,
      }));

      setValidationErrors([]);

      const students = feedbackSections?.students?.length
        ? feedbackSections.students
        : mapped.students;

      const totalScore = mapped.totalScore;
      const maxScore = mapped.rubrics.reduce(
        (acc, cp) =>
          acc + cp.rubricItems.reduce((s, it) => s + toFinite(it.points), 0),
        0,
      );

      const subject = `Grade updated for ${
        feedbackSections?.projectTitle || "submission"
      }`;
      const baseContent = `Your submission for ${
        feedbackSections?.projectTitle || "your project"
      } has been graded.\n\nTotal: ${totalScore}/${maxScore}${
        mapped.overAllFeedback
          ? `\n\nOverall feedback:\n${mapped.overAllFeedback}`
          : ""
      }`;

      if (students && students.length > 1) {
        sendMessageMutation.mutate({
          recipientId: students.map((s) => s.studentId),
          groupName:
            mapped.projectTitle || mapped.courseName || "Project Group",
          subject,
          content: baseContent,
        });
      } else if (students && students.length === 1) {
        sendMessageMutation.mutate({
          recipientId: students[0]?.studentId || "",
          subject,
          content: baseContent,
        });
      }
      onSubmit?.(serverPayload, { source: "dialog" });
      onClose();
    },
  });

  const handleSave = async () => {
    if (savingRef.current) {
      return;
    }

    // Check if any changes were made
    if (originalFeedbackSections && feedbackSections) {
      const hasChanges =
        JSON.stringify(originalFeedbackSections) !==
        JSON.stringify(feedbackSections);
      if (!hasChanges) {
        onClose();
        return;
      }
    }

    savingRef.current = true;
    setValidationErrors([]);

    if (!submissionId) {
      const msg = "Missing submissionId — cannot save";
      setValidationErrors([msg]);
      toast.error(msg);
      savingRef.current = false;
      return;
    }

    const buildRequestBodyNow = (): {
      body?: GradeSubmissionRequest;
      errors: string[];
    } => {
      const errors: string[] = [];

      if (!apiData || !apiData.data) {
        errors.push("No submission data loaded from server.");
        return { errors };
      }
      if (!feedbackSections) {
        errors.push("No feedback present.");
        return { errors };
      }

      const checkpoints = apiData.data.checkpoints ?? [];
      if (!Array.isArray(checkpoints) || checkpoints.length === 0) {
        errors.push("No checkpoints available for this submission.");
        return { errors };
      }

      const checkpointRubrics: any[] = [];

      for (const cp of checkpoints) {
        const uiCheckpoint = feedbackSections.rubrics.find(
          (r) => r.id === cp.id,
        );


        if (!Array.isArray(cp.rubrics) || cp.rubrics.length === 0) {
          errors.push(
            `Checkpoint '${cp.title ?? cp.id}' has no rubrics on server.`,
          );
          continue;
        }

        const rubricsForRequest: any[] = [];
        let checkpointTotalCalculated = 0;
        let checkpointMaxCalculated = 0;

        for (const serverRubric of cp.rubrics) {
          if (!serverRubric.id) {
            errors.push(
              `Server rubric missing id for checkpoint '${cp.title ?? cp.id}'.`,
            );
            continue;
          }
          if (
            !Array.isArray(serverRubric.criteria) ||
            serverRubric.criteria.length === 0
          ) {
            errors.push(`Rubric ${serverRubric.id} has no criteria.`);
            continue;
          }

          const criteriaScores = serverRubric.criteria.map((crit) => {
            const critName = crit.name ?? crit.description ?? "";
            const critDescription = crit.description ?? crit.name ?? "";
            let currentValue = 0;

            if (uiCheckpoint && Array.isArray(uiCheckpoint.rubricItems)) {
              const match = uiCheckpoint.rubricItems.find((ri) => {
                const riCriteria = String(ri.criteria || "").trim();
                const critNameTrimmed = String(critName).trim();
                const critDescTrimmed = String(critDescription).trim();

                if (riCriteria === critNameTrimmed) return true;
                if (riCriteria === critDescTrimmed) return true;
                if (riCriteria.toLowerCase() === critNameTrimmed.toLowerCase())
                  return true;
                if (riCriteria.toLowerCase() === critDescTrimmed.toLowerCase())
                  return true;

                return false;
              });

              if (match) {
                currentValue = toFinite(match.earnedPoints);
              } else {
                currentValue = toFinite(crit.currentScore);
              }
            } else {
              currentValue = toFinite(crit.currentScore);
            }

            const maxPoints = toFinite(crit.points);

            checkpointTotalCalculated += currentValue;
            checkpointMaxCalculated += maxPoints;

            return {
              criterionName: critName,
              score: currentValue,
              maxPoints,
            };
          });

          rubricsForRequest.push({
            rubricId: serverRubric.id,
            criteriaScores,
          });
        }

        checkpointRubrics.push({
          checkpointId: cp.id,
          totalPoints: checkpointTotalCalculated,
          maxPoints: checkpointMaxCalculated,
          rubrics: rubricsForRequest,
          feedback:
            uiCheckpoint?.checkpointFeedback ?? cp.checkpointFeedback ?? "",
        });
      }

      if (errors.length > 0) return { errors };

      // Only calculate totals for checkpoints that have been actually graded/modified
      // This prevents total mismatch errors when server expects current checkpoint only
      let totalPoints = 0;
      let maxPoints = 0;

      checkpointRubrics.forEach((checkpoint) => {
        // Check if this checkpoint has any earned points (indicating it's being graded)
        const hasEarnedPoints = checkpoint.rubrics.some((rubric: any) =>
          rubric.criteriaScores.some(
            (criteria: any) => toFinite(criteria.score) > 0,
          ),
        );

        // Check if this checkpoint has feedback (indicating it's being graded)
        const hasFeedback =
          checkpoint.feedback && checkpoint.feedback.trim().length > 0;

        // Only include in total if the checkpoint is actually being evaluated
        if (hasEarnedPoints || hasFeedback) {
          totalPoints += toFinite(checkpoint.totalPoints);
          maxPoints += toFinite(checkpoint.maxPoints);
        }
      });

      // Ensure we have at least some grading data before submitting
      if (totalPoints === 0 && maxPoints === 0) {
        const msg =
          "No checkpoint evaluations found. Please grade at least one checkpoint before submitting.";
        setValidationErrors([msg]);
        toast.error(msg);
        savingRef.current = false;
        return { errors: [msg] };
      }

      const body: GradeSubmissionRequest = {
        submissionId: submissionId!,
        checkpointRubrics,
        overallFeedback: feedbackSections.overAllFeedback,
        totalPoints,
        maxPoints,
      };

      for (const c of checkpointRubrics) {
        for (const r of c.rubrics) {
          for (const cs of r.criteriaScores) {
            if (cs.score === null || cs.score === undefined) {
              errors.push(
                `Score is null for criterion '${cs.criterionName}' in checkpoint '${c.checkpointId}'.`,
              );
            }
          }
        }

        const recomputed = c.rubrics.reduce(
          (acc: number, r: any) =>
            acc +
            r.criteriaScores.reduce(
              (s: number, cs: any) => s + toFinite(cs.score),
              0,
            ),
          0,
        );
        const recomputedMax = c.rubrics.reduce(
          (acc: number, r: any) =>
            acc +
            r.criteriaScores.reduce(
              (s: number, cs: any) => s + toFinite(cs.maxPoints),
              0,
            ),
          0,
        );

        if (Math.abs(recomputed - Number(c.totalPoints)) > 0.01) {
          errors.push(
            `Computed total ${recomputed} doesn't match reported total ${c.totalPoints} for checkpoint '${c.checkpointId}'.`,
          );
        }
        if (Math.abs(recomputedMax - Number(c.maxPoints)) > 0.01) {
          errors.push(
            `Computed max ${recomputedMax} doesn't match reported max ${c.maxPoints} for checkpoint '${c.checkpointId}'.`,
          );
        }
      }

      if (errors.length > 0) return { errors };

      return { body, errors: [] };
    };

    const { body, errors } = buildRequestBodyNow();

    if (!body) {
      const msg =
        errors.length > 0 ? errors.join("; ") : "No body generated for save";
      setValidationErrors(errors);
      toast.error(msg);
      savingRef.current = false;
      return;
    }

    body.submissionId = submissionId;

    try {
      await mutation.mutateAsync(body as GradeSubmissionRequest);
    } catch (err: any) {

      if (process.env.NODE_ENV === "development") {
        try {
          const fetchResp = await fetch("/api/grades/rubric-based", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          });
          const j = await fetchResp.json();
        } catch (fe) {
        }
      }

      const serverMsg =
        err?.response?.data?.data?.message ??
        err?.response?.data?.message ??
        err?.message ??
        "Failed to save";

      toast.error(String(serverMsg));
      savingRef.current = false;
    }
  };

  const toggleOpen = (id: string) =>
    setOpenCheckpoints((prev) => ({ ...prev, [id]: !prev[id] }));

  const updateEarnedPoints = (
    checkpointId: string,
    criteriaIndex: number,
    value: number,
  ) => {
    if (!checkpointEditable[checkpointId]) {
      return;
    }

    const v = toFinite(value);
    if (Number.isNaN(v)) {
      return;
    }

    const intVal = Math.max(0, Math.floor(v));

    // Check if this checkpoint is already evaluated
    const checkpoint = feedbackSections?.rubrics.find(
      (r) => r.id === checkpointId,
    );

    if (checkpoint) {
      const currentItem = checkpoint.rubricItems[criteriaIndex];
      const currentValue = toFinite(currentItem?.earnedPoints);
      const isAlreadyEvaluated = currentValue > 0;

      // Show confirmation if changing from a non-zero value
      if (isAlreadyEvaluated && currentValue !== intVal) {
        setPendingEdit({ checkpointId, criteriaIndex, value: intVal });
        setShowEditConfirmation(true);
        return;
      }
    }

    // Apply the update directly if not evaluated yet or same value
    applyEarnedPointsUpdate(checkpointId, criteriaIndex, intVal);
  };

  const applyEarnedPointsUpdate = (
    checkpointId: string,
    criteriaIndex: number,
    intVal: number,
  ) => {
    const intValActual = intVal;

    setFeedbackSections((prev) => {
      if (!prev) return prev;

      const updated = {
        ...prev,
        rubrics: prev.rubrics.map((r) => {
          if (r.id === checkpointId) {
            const updatedRubric = {
              ...r,
              rubricItems: r.rubricItems.map((it, idx) =>
                idx === criteriaIndex ? { ...it, earnedPoints: intVal } : it,
              ),
            };
            return updatedRubric;
          }
          return r;
        }),
      };

      return updated;
    });
  };

  const handleConfirmEdit = () => {
    if (pendingEdit) {
      applyEarnedPointsUpdate(
        pendingEdit.checkpointId,
        pendingEdit.criteriaIndex,
        pendingEdit.value,
      );
    }
    setShowEditConfirmation(false);
    setPendingEdit(null);
  };

  const handleCancelEdit = () => {
    setShowEditConfirmation(false);
    setPendingEdit(null);
  };

  const updateCheckpointFeedback = (checkpointId: string, value: string) => {
    setFeedbackSections((prev) =>
      prev
        ? {
            ...prev,
            rubrics: prev.rubrics.map((r) =>
              r.id === checkpointId ? { ...r, checkpointFeedback: value } : r,
            ),
          }
        : prev,
    );
  };

  const isBusy = isLoading || mutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white shadow-none"
        style={{
          width: 600,
          maxWidth: "95%",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="flex items-center space-x-2">
                <span className="text-lg font-semibold text-bits-grey-900 mb-4">
                  Feedback
                </span>
              </DialogTitle>
              <div className="flex flex-row ">
                <div className="flex flex-col">
                  <div className="flex flex-row gap-3 mb-2">
                    <User className="h-5 w-5 text-bits-grey-500" />
                    <span className="text-sm font-medium text-bits-grey-600">
                      {feedbackSections?.projectType
                        ? "Team Members"
                        : "Submitted By"}
                    </span>
                  </div>

                  {(
                    feedbackSections?.students || mockFeedbackSection.students
                  ).map((student) => (
                    <div
                      key={student.studentId}
                      className=" ml-8 flex flex-col gap-2"
                    >
                      <div className=" flex flex-row gap-3 mb-2">
                        <div className="h-10 w-10 rounded-full flex bg-bits-blue items-center justify-center">
                          <User className="h-5 w-5 text-white" />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-md font-semibold text-bits-grey-700">
                            {student.studentName}
                          </span>
                          {/* <span className="text-sm font-normal text-bits-grey-600">
                            {student.studentId}
                          </span> */}
                          <span className="text-sm font-normal text-bits-grey-600">
                            {student.studentEmail}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col">
                  <div className="flex flex-row gap-3 mb-2">
                    <FileText className="h-5 w-5 text-bits-grey-500" />
                    <span className="text-sm font-medium text-bits-grey-600">
                      Project & Course
                    </span>
                  </div>

                  <div className=" ml-8 flex flex-col gap-2">
                    <span className="text-md font-semibold text-bits-grey-700">
                      {feedbackSections?.projectTitle ??
                        apiData?.data?.project?.title ??
                        "—"}
                    </span>
                    <span className="text-sm font-normal text-bits-grey-600">
                      {" "}
                      {feedbackSections?.courseCode ??
                        apiData?.data?.project?.course?.code ??
                        ""}{" "}
                      -{" "}
                      {feedbackSections?.courseName ??
                        apiData?.data?.project?.course?.name ??
                        ""}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-xl text-bits-grey-900 font-semibold">
              Assessment Rubric
            </h3>
            <div className="text-right">
              <div className="flex flex-row gap-3">
                <span className="text-md font-medium text-bits-grey-600">
                  Total Score
                </span>
                <div
                  className={`display-xs font-bold  ${getGradeColor(
                    totalEarned,
                    totalMax,
                  )}`}
                >
                  {totalEarned}/{totalMax}
                </div>
              </div>
            </div>
          </div>
        </div>

        {isBusy && !feedbackSections ? (
          <div className="py-8 flex flex-col items-center justify-center">
            <div className="text-sm text-bits-grey-500">
              Loading submission...
            </div>
          </div>
        ) : (
          (feedbackSections ?? mockFeedbackSection).rubrics.map((ru) => {
            const isOpen = Boolean(openCheckpoints[ru.id]);
            const isEditable = checkpointEditable[ru.id];
            const isEvaluated = isRubricEvaluated(ru);

            return (
              <Card key={ru.id} className="rounded-lg shadow-sm">
                <CardHeader
                  onClick={() => toggleOpen(ru.id)}
                  className="cursor-pointer flex items-center px-4 py-3"
                  role="button"
                  aria-expanded={isOpen}
                >
                  <div className="w-full flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Flag
                        className={`h-4 w-4 ${
                          isEvaluated ? "text-green-600" : "text-bits-indigo"
                        }`}
                      />
                      <CardTitle
                        className={`text-lg font-normal m-0 ${
                          !isEditable
                            ? "text-bits-grey-300"
                            : "text-bits-grey-400"
                        }`}
                      >
                        {ru.checkPointName}
                      </CardTitle>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="text-lg font-bold text-bits-grey">
                        {ru.rubricItems.reduce(
                          (s, it) => s + toFinite(it.earnedPoints),
                          0,
                        )}
                        /
                        {ru.rubricItems.reduce(
                          (s, it) => s + toFinite(it.points),
                          0,
                        )}
                      </div>
                      {isEditable ? (
                        <Edit className="h-4 w-4 text-bits-blue" />
                      ) : null}
                    </div>
                  </div>
                </CardHeader>

                {isOpen && (
                  <CardContent className="px-4 pt-1 pb-3">
                    <Card className="p-2 mb-1 rounded-lg shadow-xs">
                      <div className="space-y-2">
                        {ru.rubricItems.map((rubricItem, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between"
                          >
                            <Label className="text-md font-normal text-bits-grey">
                              {rubricItem.criteria}
                            </Label>

                            <div className="flex items-center gap-2">
                              <Select
                                value={String(
                                  toFinite(rubricItem.earnedPoints),
                                )}
                                onValueChange={(value) => {
                                  const n = Number(value);
                                  updateEarnedPoints(
                                    ru.id,
                                    i,
                                    Number.isNaN(n)
                                      ? 0
                                      : Math.max(0, Math.floor(n)),
                                  );
                                }}
                                disabled={!isEditable}
                              >
                                <SelectTrigger className="text-md font-normal text-bits-grey-500 bg-bits-grey-100 py-2">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent
                                  className="text-md font-normal text-bits-grey-500 max-h-[200px]"
                                  position="popper"
                                  sideOffset={5}
                                >
                                  {Array.from(
                                    {
                                      length: Math.max(
                                        0,
                                        Math.floor(
                                          toFinite(rubricItem.points),
                                        ) + 1,
                                      ),
                                    },
                                    (_, v) => (
                                      <SelectItem key={v} value={String(v)}>
                                        {v}
                                      </SelectItem>
                                    ),
                                  )}
                                </SelectContent>
                              </Select>

                              <span className="text-md font-medium text-bits-grey-600">
                                /{rubricItem.points}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>

                    <div className="flex flex-col">
                      <Label className="text-sm font-medium text-bits-grey-700">
                        Feedback
                      </Label>
                      <Textarea
                        value={ru.checkpointFeedback ?? ""}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                          updateCheckpointFeedback(ru.id, e.target.value)
                        }
                        className="bg-bits-grey-100 text-md font-normal text-bits-grey-500 h-20 p-3"
                      />
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })
        )}

        {validationErrors.length > 0 && (
          <div className="my-4 p-3 border border-red-200 bg-red-50 rounded">
            <ul className="list-disc list-inside text-sm text-red-700">
              {validationErrors.map((e, idx) => (
                <li key={idx}>{e}</li>
              ))}
            </ul>
          </div>
        )}

        {(() => {
          // Show Overall Feedback only when all checkpoints have been evaluated
          if (
            !feedbackSections?.rubrics ||
            feedbackSections.rubrics.length === 0
          )
            return null;

          const allCheckpointsEvaluated = feedbackSections.rubrics.every(
            (rubric) => {
              // Check if rubric has any earned points or feedback
              const hasEarnedPoints = rubric.rubricItems.some(
                (item) => toFinite(item.earnedPoints) > 0,
              );
              const hasFeedback =
                rubric.checkpointFeedback &&
                rubric.checkpointFeedback.trim().length > 0;
              return hasEarnedPoints || hasFeedback;
            },
          );

          if (!allCheckpointsEvaluated) return null;

          return (
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium text-bits-grey-700">
                Overall Feedback
              </Label>
              <Textarea
                className="bg-bits-grey-100 text-md font-normal text-bits-grey-500 h-20"
                value={
                  feedbackSections?.overAllFeedback ??
                  mockFeedbackSection.overAllFeedback
                }
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setFeedbackSections((prev) =>
                    prev
                      ? { ...prev, overAllFeedback: e.target.value }
                      : {
                          ...mockFeedbackSection,
                          overAllFeedback: e.target.value,
                        },
                  )
                }
              />
            </div>
          );
        })()}

        <div className="flex flex-row space-x-2 w-full">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
            disabled={isBusy}
          >
            Cancel
          </Button>
          <Button
            variant="bits"
            onClick={handleSave}
            className="flex-1 bg-bits-blue hover:bg-bits-blue/90"
            disabled={isBusy}
          >
            {mutation.isPending ? "Saving..." : "Save Grade"}
          </Button>
        </div>
      </DialogContent>

      {/* Edit Confirmation Dialog */}
      <AlertDialog
        open={showEditConfirmation}
        onOpenChange={setShowEditConfirmation}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Edit grade?</AlertDialogTitle>
            <AlertDialogDescription>
              This checkpoint is already evaluated. Do you want to update the
              score?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelEdit}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmEdit}
              className="bg-bits-blue hover:bg-bits-blue/90"
            >
              Edit grade
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
